import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

import SwipeableTrayTrigger from './SwipeableTrayTrigger';
import VehicleCard from './VehicleCard';
import HeightUpdater from './HeightUpdater';

const SWIPE_THRESHOLD = 150; // px of vertical drag to trigger open/close (slow drag)
const SWIPE_VELOCITY_THRESHOLD = 0.3;
const TRANSITION_DURATION = 350; // ms (matches react-swipeable-views '0.35s')
const TRANSITION_EASE = 'cubic-bezier(0.15, 0.3, 0.25, 1)';

const SwipeableTray = ({
	bodyStyle,
	children,
	defaultOpen = false,
	fullScreen = false,
	marginTop = 0,
	onChange,
	overflowHeight = 0,
	overlay = true,
	overlayStyle,
	shadowTip = true,
	style,
	swipeableViewsProps = {},
	topShadow = true,
	currentStep
}) => {
	const [windowHeight, setHeight] = useState(window.innerHeight);
	const [open, setOpen] = useState(defaultOpen);

	// DOM ref for imperative translateY and native touch listeners
	const swiperRef = useRef(null);
	// Drag start Y position; null when not dragging
	const dragStartY = useRef(null);
	// Drag start timestamp for velocity calculation
	const dragStartTime = useRef(null);
	// Mirrors `open` state for stale-closure-free reads in native listeners
	const openRef = useRef(defaultOpen);
	// Track if we're in a drag operation to avoid interfering with content interactions
	const isDragOperation = useRef(false);

	const onHeightChange = (newHeight) => {
		setHeight(newHeight);
	};

	const onChangeIndex = useCallback(
		(index) => {
			const isOpen = index === 1;
			openRef.current = isOpen;
			setOpen(isOpen);
			if (onChange !== undefined) {
				onChange(isOpen);
			}
			if (index === 0) {
				document.getElementById('sv-children-container').scrollTop = 0;
			}
			if (swipeableViewsProps.onTransitionEnd) {
				setTimeout(
					swipeableViewsProps.onTransitionEnd,
					TRANSITION_DURATION
				);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[onChange, swipeableViewsProps.onTransitionEnd]
	);

	// Native (non-passive) touch listeners — React synthetic events are passive
	// so preventDefault() is a no-op there; we must use addEventListener here.
	useEffect(() => {
		const el = swiperRef.current;
		if (!el) return undefined;

		const onTouchStart = (e) => {
			// Store touch start position
			dragStartY.current = e.touches[0].clientY;
			dragStartTime.current = Date.now();

			// If tray is closed, immediately start drag to open
			if (!openRef.current) {
				e.preventDefault();
				isDragOperation.current = true;
			} else {
				// Tray is open - don't commit to drag yet, let onTouchMove decide based on gesture
				isDragOperation.current = false;
			}
		};

		const onTouchMove = (e) => {
			// If no drag started, nothing to do
			if (dragStartY.current === null) return;

			const deltaY = e.touches[0].clientY - dragStartY.current;
			const scrollableContainer = document.getElementById(
				'sv-children-container'
			);

			// If we haven't committed to a drag operation yet (means tray is open)
			if (!isDragOperation.current) {
				const absY = Math.abs(deltaY);
				const MOVEMENT_THRESHOLD = 10; // Require 10px of movement to decide intent

				// Not enough movement yet to determine intent
				if (absY < MOVEMENT_THRESHOLD) {
					return;
				}

				const isScrolled =
					scrollableContainer && scrollableContainer.scrollTop > 0;
				const canScroll =
					scrollableContainer &&
					scrollableContainer.scrollHeight >
						scrollableContainer.clientHeight;

				// User is dragging down
				if (deltaY > 0) {
					// If at top of scroll, interpret as tray close gesture
					if (!isScrolled || !canScroll) {
						e.preventDefault();
						isDragOperation.current = true;
					}
					// If scrolled down, let it scroll
					else {
						return;
					}
				}
				// User is dragging up (scrolling content down)
				else {
					// If there's scrollable content, allow scrolling
					if (canScroll) {
						return; // Let native scrolling happen
					}
					// No scrollable content, ignore the gesture
					dragStartY.current = null;
					dragStartTime.current = null;
					return;
				}
			}

			// We're in an active drag operation - handle tray dragging
			e.preventDefault();

			if (el) {
				const base = openRef.current
					? 0
					: el.offsetHeight - overflowHeight;
				const maxTranslate = el.offsetHeight - overflowHeight;
				const clamped = Math.max(
					0,
					Math.min(base + deltaY, maxTranslate)
				);
				el.style.transition = 'none'; // instant finger tracking
				el.style.transform = `translateY(${clamped}px)`;
			}
		};

		const onTouchEnd = (e) => {
			// If no drag operation started, just clean up and exit
			if (dragStartY.current === null || !isDragOperation.current) {
				dragStartY.current = null;
				dragStartTime.current = null;
				return;
			}

			const deltaY = e.changedTouches[0].clientY - dragStartY.current;
			const elapsed = Date.now() - dragStartTime.current;

			// Reset drag state
			dragStartY.current = null;
			dragStartTime.current = null;
			isDragOperation.current = false;

			if (el) {
				el.style.transition = `transform ${TRANSITION_DURATION}ms ${TRANSITION_EASE}`;
			}

			const velocity = Math.abs(deltaY) / elapsed;
			const isIntentional =
				Math.abs(deltaY) >= SWIPE_THRESHOLD ||
				velocity >= SWIPE_VELOCITY_THRESHOLD;

			if (!isIntentional) {
				if (el) {
					el.style.transform = openRef.current
						? 'translateY(0)'
						: `translateY(calc(100% - ${overflowHeight}px))`;
				}
				return;
			}

			if (deltaY < 0 && !openRef.current) {
				onChangeIndex(1);
			} else if (deltaY > 0 && openRef.current) {
				onChangeIndex(0);
			} else if (el) {
				// Direction didn't match tray state — snap back
				el.style.transform = openRef.current
					? 'translateY(0)'
					: `translateY(calc(100% - ${overflowHeight}px))`;
			}
		};

		el.addEventListener('touchstart', onTouchStart, { passive: false });
		el.addEventListener('touchmove', onTouchMove, { passive: false });
		el.addEventListener('touchend', onTouchEnd, { passive: false });

		return () => {
			el.removeEventListener('touchstart', onTouchStart);
			el.removeEventListener('touchmove', onTouchMove);
			el.removeEventListener('touchend', onTouchEnd);
		};
	}, [onChangeIndex, overflowHeight]);

	const bodyMaxHeight = windowHeight - marginTop - 25;

	const styles = {
		root: {
			height: overflowHeight,
			position: 'fixed',
			bottom: 0,
			right: 0,
			left: 0,
			zIndex: 1,
			...style
		},
		swiper: {
			root: {
				position: 'fixed',
				bottom: 0,
				left: 0,
				right: 0,
				overflowY: 'initial',
				boxSizing: 'border-box',
				transition: `transform ${TRANSITION_DURATION}ms ${TRANSITION_EASE}`,
				// translateY(0) when open; slides below viewport when closed
				transform: open
					? 'translateY(0)'
					: `translateY(calc(100% - ${overflowHeight}px))`,
				...(topShadow && {
					boxShadow: 'rgba(0, 0, 0, 0.156863) 0px -6px 5px'
				}),
				...swipeableViewsProps.style,
				...swipeableViewsProps.containerStyle
			},
			body: {
				overflow: open ? 'auto' : 'hidden',
				backgroundColor: 'white',
				height: fullScreen ? bodyMaxHeight : 'initial',
				maxHeight: bodyMaxHeight,
				...bodyStyle
			}
		},
		overlay: {
			position: 'fixed',
			top: 0,
			right: 0,
			left: 0,
			height: windowHeight,
			transition: 'opacity 450ms',
			pointerEvents: 'none',
			backgroundColor: 'black',
			opacity: 0,
			...(open && { opacity: 0.54, pointerEvents: 'auto' }),
			...overlayStyle
		},
		shadowTip: {
			position: 'fixed',
			height: 60,
			width: '200%',
			bottom: -60,
			left: '-50%',
			boxShadow: 'rgba(0, 0, 0, 0.7) 0px 0px 30px',
			transition: 'transform 450ms',
			transform: open ? 'translateY(50px)' : 'translateY(0)'
		}
	};

	return (
		<div data-testid="swipeable-tray-root" style={styles.root}>
			<HeightUpdater
				height={windowHeight}
				onHeightChange={onHeightChange}
			/>
			{overlay && (
				<div
					data-testid="overlay"
					style={styles.overlay}
					onClick={() => onChangeIndex(0)}
					role="presentation"
					aria-hidden="true"
				/>
			)}
			<div
				data-testid="swipeable-views-container"
				ref={swiperRef}
				style={styles.swiper.root}
			>
				<div
					id="sv-children-container"
					data-testid="swipeable-tray-wrapper"
					style={styles.swiper.body}
					className={`border-top rounded-top ReactSwipeableTray--${
						open ? 'open' : 'closed'
					}`}
					role="button"
					tabIndex={0}
				>
					{children}
					<SwipeableTrayTrigger />
					<VehicleCard
						onChangeIndex={onChangeIndex}
						useTray
						currentStep={currentStep}
					/>
				</div>
			</div>
			{shadowTip && (
				<div data-testid="shadow-tip" style={styles.shadowTip} />
			)}
		</div>
	);
};

SwipeableTray.propTypes = {
	bodyStyle: PropTypes.shape({}),
	children: PropTypes.node.isRequired,
	defaultOpen: PropTypes.bool,
	fullScreen: PropTypes.bool,
	marginTop: PropTypes.number,
	onChange: PropTypes.func,
	overflowHeight: PropTypes.number,
	overlay: PropTypes.bool,
	overlayStyle: PropTypes.shape({}),
	shadowTip: PropTypes.bool,
	style: PropTypes.shape({}),
	swipeableViewsProps: PropTypes.shape({}),
	topShadow: PropTypes.bool,
	currentStep: PropTypes.string
};

export default SwipeableTray;
